import type { Table } from 'dexie'
import { localDb } from '@/data/local/database'
import type { PendingOperation, SyncEntity, SyncEntityType } from '@/domain/types'
import { getActiveIdentity } from '@/services/identity/local-identity'
import { remoteApiRequest } from '@/data/api/remote-api'

export function createEntityId(prefix = 'entity') {
  return `${prefix}_${crypto.randomUUID()}`
}

/**
 * 服务器优先：立即把单条操作推送到服务器。
 * 成功返回服务器 version；失败（网络/服务器不可用/冲突）返回 null。
 * operationId 在服务器端幂等（sync_operations 主键），失败回退 pending 后
 * 下次重推不会产生重复数据。
 */
async function tryPushImmediate(operation: PendingOperation): Promise<number | null> {
  try {
    const response = await remoteApiRequest('sync/push', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ workspaceId: operation.workspaceId, operations: [operation] }),
      // 短超时：离线/慢网时快速回退本地优先，避免保存卡顿
      signal: AbortSignal.timeout(8000),
    })
    if (!response.ok) return null
    const data = await response.json() as { accepted?: Array<{ entityId?: string; version?: number }> }
    const accepted = data.accepted?.find(item => item.entityId === operation.entityId)
    return accepted?.version ?? null
  } catch {
    return null
  }
}

export async function putLocalEntity<T extends SyncEntity, TInsert>(
  table: Table<T, string, TInsert>,
  entityType: SyncEntityType,
  entity: T,
  action: 'create' | 'update',
  baseVersion: number | null,
) {
  const identity = await getActiveIdentity()
  const operation: PendingOperation = {
    operationId: createEntityId('op'),
    deviceId: identity.deviceId,
    workspaceId: entity.workspaceId,
    entityType,
    entityId: entity.id,
    action,
    baseVersion,
    clientUpdatedAtMs: entity.updatedAtMs,
    payload: entity as unknown as Record<string, unknown>,
    retryCount: 0,
    nextRetryAtMs: Date.now(),
    createdAtMs: Date.now(),
  }
  // 服务器优先：先上传，成功则本地直接落库（不产生 pending）
  const serverVersion = await tryPushImmediate(operation)
  if (serverVersion !== null) {
    const synced = { ...entity, version: serverVersion } as T
    await table.put(synced as unknown as TInsert)
  } else {
    // 上传失败：回退本地优先（落库 + pending，稍后重试）
    await localDb.transaction('rw', table, localDb.pendingOperations, async () => {
      await table.put(entity as unknown as TInsert)
      await localDb.pendingOperations.add(operation)
    })
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('study-tool:local-change', { detail: { entityType, entityId: entity.id } }))
  }
  return entity
}

export async function softDeleteLocalEntity<T extends SyncEntity, TInsert>(
  table: Table<T, string, TInsert>,
  entityType: SyncEntityType,
  id: string,
) {
  const entity = await table.get(id)
  if (!entity || entity.deletedAtMs) return null
  const identity = await getActiveIdentity()
  const now = Date.now()
  const deleted = { ...entity, deletedAtMs: now, updatedAtMs: now } as T
  const operation: PendingOperation = {
    operationId: createEntityId('op'),
    deviceId: identity.deviceId,
    workspaceId: entity.workspaceId,
    entityType,
    entityId: id,
    action: 'delete',
    baseVersion: entity.version,
    clientUpdatedAtMs: now,
    payload: {},
    retryCount: 0,
    nextRetryAtMs: now,
    createdAtMs: now,
  }
  // 服务器优先：先上传删除，成功则本地直接软删（不产生 pending）
  const serverVersion = await tryPushImmediate(operation)
  if (serverVersion !== null) {
    await table.put({ ...deleted, version: serverVersion } as unknown as TInsert)
  } else {
    // 上传失败：回退本地优先
    await localDb.transaction('rw', table, localDb.pendingOperations, async () => {
      await table.put(deleted as unknown as TInsert)
      await localDb.pendingOperations.add(operation)
    })
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('study-tool:local-change', { detail: { entityType, entityId: id } }))
  }
  return deleted
}
